<template>
    <loader v-if="loading" />
    <div v-else-if="!loading">
        <v-row>
            <v-col>
                <v-btn
                    color="green"
                    class="white--text"
                    @click="showNewReportDialog = true"
                    :loading="submitting"
                >
                    <v-icon left>
                        fas fa-plus
                    </v-icon>
                    Ny rapport
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="6">
                <v-card>
                    <v-subheader>
                        <v-row no-gutters>
                            <v-col>
                                Rapporter
                            </v-col>
                        </v-row>
                    </v-subheader>
                    <v-divider></v-divider>
                    <v-card-text v-if="!reports.length">Der er ingen rapporter.</v-card-text>
                    <v-list class="striped-list" v-else>
                        <v-list-item-group v-model="selectedReportIndex">
                            <v-list-item v-for="(report, index) in reports" :key="index">
                                <v-list-item-content>
                                    <v-list-item-title>                         
                                        <v-row no-gutters>
                                            <v-col>
                                                {{ report.name }}
                                            </v-col>
                                        </v-row>
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-card>
            </v-col>
            <v-col cols="6">
                <v-card>
                    <v-card-text v-if="!selectedReport">
                        Vælg en rapport til venstre.
                    </v-card-text>
                    <v-card-text v-else>
                        <v-row>
                            <v-col class="text-right">
                                <v-btn depressed outlined class="white--text" color="red" @click="showDeleteReportDialog = true">
                                    <v-icon left>fa-trash-alt</v-icon>
                                    Slet rapport
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-form ref="updateReportForm" lazy-validation>
                            <v-text-field label="Navn" v-model="selectedReport.name" :rules="[v => !!v || 'Navn skal udfyldes']"></v-text-field>
                            <v-select label="Type" v-model="selectedReport.type" :items="reportTypes"></v-select>
                            <v-row>
                                <v-col>
                                    <v-btn outlined @click="showEditCampaignsDialog = true">
                                        <v-icon left>fa-pen</v-icon>
                                        Rediger kampagner
                                    </v-btn>
                                </v-col>
                                <v-col class="text-right">
                                    <v-btn depressed class="white--text" color="green" @click="updateReport(selectedReport)">
                                        <v-icon left>fa-save</v-icon>
                                        Gem ændringer
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-dialog v-if="selectedReport" v-model="showEditCampaignsDialog" width="900">
            <v-card>
                <v-card-title>Rediger kampagner i "{{ selectedReport.name }}"</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                    <v-row v-if="selectedReport.type === 'grouped'" align="center">
                        <v-col cols="6">
                            <v-autocomplete
                                v-model="selectedGroup"
                                outlined
                                class="mt-0 pt-0"
                                hide-details
                                single-line
                                label="Gruppe"
                                :items="mapGroupsToSelect(selectedReport.groups)"
                            />
                        </v-col>
                        <v-col v-if="selectedGroup" cols="3" class="text-right">
                            <v-btn
                                depressed
                                outlined
                                color="red"
                                class="white--text"
                                @click="deleteGroup"
                                :loading="submitting"
                            >
                                <v-icon left>
                                    fas fa-trash-alt
                                </v-icon>
                                Slet gruppe
                            </v-btn>
                        </v-col>
                        <v-col v-else cols="3">
                        </v-col>
                        <v-col class="text-right">
                            <v-btn
                                depressed
                                color="green"
                                class="white--text"
                                @click="showNewGroupDialog = true"
                                :loading="submitting"
                            >
                                <v-icon left>
                                    fas fa-plus
                                </v-icon>
                                Ny Gruppe
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-divider v-if="selectedReport.type === 'grouped'"></v-divider>
                    <v-row>
                        <v-col>
                            <v-text-field
                                append-icon="fa-search"
                                single-line
                                label="Søg"
                                outlined
                                hide-details
                                v-model="searchString"
                                clearable
                            >
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-subheader>Alle kampagner</v-subheader>
                            <v-divider></v-divider>
                            <v-card-text v-if="!availableCampaigns.length">Ingen kampagner matcher din søgning</v-card-text>
                            <v-list>
                                <v-virtual-scroll :items="availableCampaigns" :item-height="50" height="250">
                                    <template v-slot="{ item }">
                                        <v-list-item @click="addCampaign(item.uniqueId, selectedGroup)">
                                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                                        </v-list-item>
                                        <v-divider></v-divider>
                                    </template>
                                </v-virtual-scroll>
                            </v-list>
                        </v-col>
                        <v-col>
                            <v-subheader>Valgte kampagner</v-subheader>
                            <v-divider></v-divider>
                            <v-card-text v-if="selectedReport.type === 'grouped' && !selectedGroup">Du har ikke valgt nogen gruppe</v-card-text>
                            <v-card-text v-else-if="!selectedReport.campaigns.length">Du har ikke valgt nogen kampagner</v-card-text>
                            <v-card-text v-else-if="!selectedCampaigns.length">Ingen af de valgte kampagner matcher din søgning</v-card-text>
                            <v-list v-else>
                                <v-virtual-scroll :items="selectedCampaigns" :item-height="50" height="250">
                                    <template slot-scope="props">
                                        <v-list-item @click="removeCampaign(props.item.uniqueId)" :key="props.itemKey">
                                            <v-list-item-title>{{ props.item.name }}</v-list-item-title>
                                        </v-list-item>
                                        <v-divider></v-divider>
                                    </template>
                                </v-virtual-scroll>
                            </v-list>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showEditCampaignsDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="updateReport(selectedReport)" :loading="submitting">Gem ændringer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showNewGroupDialog" width="500">
            <v-card>
                <v-card-title>Ny gruppe</v-card-title>
                <v-card-text>
                    <v-form ref="newGroupForm" lazy-validation>
                        <v-text-field label="Navn" v-model="newGroupName" :rules="[v => !!v || 'Navn skal udfyldes']"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showNewGroupDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="createGroup" :loading="submitting">Opret</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showNewReportDialog" width="500">
            <v-card>
                <v-card-title>Ny rapport</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                    <v-form ref="newReportForm" lazy-validation>
                        <v-text-field label="Navn" v-model="newReport.name" :rules="[v => !!v || 'Navn skal udfyldes']"></v-text-field>
                        <v-select label="Type" v-model="newReport.type" :items="reportTypes"></v-select>
                    </v-form>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showNewReportDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="createReport" :loading="submitting">Opret</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="selectedReport" v-model="showDeleteReportDialog" width="600">
            <v-card>
                <v-card-title>Er du sikker på, at du vil slette rapporten "{{ selectedReport.name }}"?</v-card-title>
                <v-divider></v-divider>
                <v-card-actions class="justify-end">
                    <v-btn class="red--text" text @click="showDeleteReportDialog = false">Annuller</v-btn>
                    <v-btn class="green--text" text @click="deleteReport(selectedReport)" :loading="submitting">Bekræft</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    data () {
        return {
            showNewReportDialog: false,
            showDeleteReportDialog: false,
            showEditCampaignsDialog: false,
            showNewGroupDialog: false,
            reports: null,
            loading: false,
            submitting: false,
            currentReportIndex: null,
            newReport: {
                name: null,
                type: 'standard'
            },
            newGroupName: null,
            reportTypes: [
                {
                    text: 'Standard',
                    value: 'standard'
                },
                {
                    text: 'Grupperet',
                    value: 'grouped'
                }
            ],
            selectedReportIndex: null,
            selectedReport: null,
            searchString: '',
            selectedGroup: null
        }
    },
    computed: {
        availableCampaigns () {
            return this.$store.getters['main/getCampaigns'].filter(campaign => {
                const matchesSearch = !this.searchString || campaign.name.replace(/[_\s]/gi, '').toLowerCase().includes(this.searchString.replace(/[_\s]/gi, '').toLowerCase())
                const selected = this.selectedReport.campaigns.includes(campaign.uniqueId)
                return matchesSearch && !selected
            })
        },
        selectedCampaigns () {
            if (this.selectedReport.type === 'grouped') {
                return this.$store.getters['main/getCampaigns'].filter(campaign => {
                    const matchesSearch = !this.searchString || campaign.name.replace(/[_\s]/gi, '').toLowerCase().includes(this.searchString.replace(/[_\s]/gi, '').toLowerCase())
                    const selected = this.selectedReport.groups.find(group => group._id === this.selectedGroup).campaigns.includes(campaign.uniqueId)
                    return matchesSearch && selected
                })
            } else {
                return this.$store.getters['main/getCampaigns'].filter(campaign => {
                    const matchesSearch = !this.searchString || campaign.name.replace(/[_\s]/gi, '').toLowerCase().includes(this.searchString.replace(/[_\s]/gi, '').toLowerCase())
                    const selected = this.selectedReport.campaigns.includes(campaign.uniqueId)
                    return matchesSearch && selected
                })
            }
        }
    },
    methods: {
        mapGroupsToSelect (groups) {
            return groups.map(group => {
                return {
                    text: group.name,
                    value: group._id
                }
            })
        },
        async getData() {
            this.loading = true
            this.selectedReportIndex = null
            this.showNewReportDialog = false,
            this.showDeleteReportDialog = false,
            this.showEditCampaignsDialog = false,
            this.searchString = null
            const response = await this.$http().get('/controlpanel/reports')
            this.reports = response.data
            this.loading = false
        },
        async createReport() {
            this.submitting = true
            if (this.$refs.newReportForm.validate()) {
                const response = await this.$http().post("/controlpanel/reports", this.newReport)
                if (response.status === 201) {
                    this.reports.push(response.data)
                }
            }
            this.submitting = false
            this.showNewReportDialog = false
        },
        async updateReport(report) {
            this.submitting = true
            if (this.$refs.updateReportForm.validate()) {
                const response = await this.$http().patch("/controlpanel/reports", {
                    id: report._id, 
                    name: report.name,
                    type: report.type,
                    campaigns: report.campaigns,
                    groups: report.groups
                })
                if (response.status === 204) this.getData()
            }
            this.submitting = false
        },
        async deleteReport(report) {
            this.submitting = true
            if (this.$refs.updateReportForm.validate()) {
                const response = await this.$http().delete(`/controlpanel/reports/${report._id}`)
                if (response.status === 204) {
                    this.getData()
                    this.showDeleteReportDialog = false
                }
            }
            this.submitting = false
        },
        async createGroup () {
            this.submitting = true
            if (this.$refs.newGroupForm.validate()) {
                const response = await this.$http().post(`/controlpanel/reports/${this.selectedReport._id}/groups`, {
                    name: this.newGroupName
                })
                if (response.status === 201) {
                    this.showNewGroupDialog = false
                    this.selectedReport.groups.push(response.data)
                    this.selectedGroup = response.data._id
                }
            }
            this.submitting = false
        },
        async deleteGroup () {
            this.submitting = true
            const group = this.reports[this.selectedReportIndex].groups.find(group => group._id === this.selectedGroup)
            const response = await this.$http().delete(`/controlpanel/reports/${this.selectedReport._id}/groups/${this.selectedGroup}`)
            if (response.status === 204) {
                this.selectedGroup = null
                this.selectedReport.groups.splice(this.selectedReport.groups.indexOf(group), 1)
                this.currentGroup = null
            }
            this.submitting = false
        },
        addCampaign (uniqueId) {
            if (this.selectedReport.type === 'grouped' && !this.selectedGroup) return null
            this.selectedReport.campaigns.push(uniqueId)
            if (this.selectedReport.type === 'grouped') {
                const selectedReportGroups = this.selectedReport.groups.find(group => group._id === this.selectedGroup)
                selectedReportGroups.campaigns.push(uniqueId)
            }
        },
        removeCampaign (uniqueId) {
            this.selectedReport.campaigns.splice(this.selectedReport.campaigns.indexOf(uniqueId), 1)
            if (this.selectedReport.type === 'grouped') {
                const selectedReportGroups = this.selectedReport.groups.find(group => group._id === this.selectedGroup)
                selectedReportGroups.campaigns.splice(selectedReportGroups.campaigns.indexOf(uniqueId), 1)
            }
        }
    },
    watch: {
        selectedReportIndex: function () {
            if (this.selectedReportIndex === null || typeof this.selectedReportIndex === 'undefined') {
                this.selectedReport = null
            } else {
                this.selectedReport = { ...this.reports[this.selectedReportIndex] }
            }
        }
    },
    created () {
        this.getData()
    }
}
</script>